import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Video as VideoIcon, User, Mic, XCircle, CheckCircle2, Settings as SettingsIcon, Copy, Loader2, RefreshCw, Smartphone, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSettingsStatus, getHeygenAvatars, getHeygenVoices } from "@/lib/api";
import { heygenCreationEnabled, configState } from "@/lib/providerFeatures";

const ConfigBadge = ({ state, activeText, testid }) => {
  if (state === "configured") {
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700" data-testid={testid}>
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> {activeText}
      </div>
    );
  }
  const text = state === "loading" ? "Memuat status…" : state === "error" ? "Status tidak dapat dimuat" : "Belum dikonfigurasi";
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500" data-testid={testid}>
      <XCircle className="h-3.5 w-3.5 text-slate-400" /> {text}
    </div>
  );
};

const CARDS = [
  { key: "openai", field: "connected", title: "OpenAI API", desc: "Untuk pembuatan skrip otomatis.", icon: Bot, onText: "Terhubung", offText: "Belum Terhubung", testid: "settings-openai" },
  { key: "heygen", field: "connected", title: "HeyGen API", desc: "Untuk pembuatan video avatar.", icon: VideoIcon, onText: "Terhubung", offText: "Belum Terhubung", testid: "settings-heygen" },
  { key: "avatar", field: "configured", title: "Avatar", desc: "Karakter avatar untuk video.", icon: User, onText: "Dikonfigurasi", offText: "Belum Dikonfigurasi", testid: "settings-avatar" },
  { key: "voice", field: "configured", title: "Suara", desc: "Suara narasi untuk video.", icon: Mic, onText: "Dikonfigurasi", offText: "Belum Dikonfigurasi", testid: "settings-voice" },
];

export default function Settings() {
  const [status, setStatus] = useState(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const [avatars, setAvatars] = useState(null);
  const [voices, setVoices] = useState(null);
  const [loadingAvatars, setLoadingAvatars] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);

  useEffect(() => {
    getSettingsStatus().then(setStatus).catch(() => setStatusFailed(true));
  }, []);

  const loadAvatars = async () => {
    setLoadingAvatars(true);
    try {
      const data = await getHeygenAvatars();
      setAvatars(data.avatars || []);
      toast.success(`${(data.avatars || []).length} avatar dimuat`);
    } catch (e) {
      toast.error("Gagal memuat avatar dari HeyGen. Silakan coba lagi.");
    } finally {
      setLoadingAvatars(false);
    }
  };

  const loadVoices = async () => {
    setLoadingVoices(true);
    try {
      const data = await getHeygenVoices();
      setVoices(data.voices || []);
      toast.success(`${(data.voices || []).length} suara dimuat`);
    } catch (e) {
      toast.error("Gagal memuat suara dari HeyGen. Silakan coba lagi.");
    } finally {
      setLoadingVoices(false);
    }
  };

  const copyId = (id) => {
    navigator.clipboard?.writeText(id);
    toast.success("ID disalin");
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Pengaturan</p>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          Pengaturan
        </h1>
        <p className="mt-2 text-slate-500 md:text-lg">Status koneksi integrasi. Kunci API tidak pernah ditampilkan.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="settings-grid">
        {CARDS.map(({ key, field, title, desc, icon: Icon, onText, offText, testid }) => {
          // HeyGen creation is switched off server-side; a present API key must not read as an active feature.
          const heygenPaused = key === "heygen" && !heygenCreationEnabled(status);
          const active = !heygenPaused && status?.[key]?.[field] === true;
          return (
            <div
              key={key}
              data-testid={testid}
              className="rounded-xl border border-slate-200 bg-white p-6 flex items-start gap-4"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
                <p className="mt-1 text-sm text-slate-500">{desc}</p>
                <div
                  className={`mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
                    active
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-slate-200 bg-slate-50 text-slate-500"
                  }`}
                  data-testid={`${testid}-status`}
                >
                  {active ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-slate-400" />
                  )}
                  {heygenPaused ? "Dinonaktifkan sementara" : active ? onText : offText}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Read-only konfigurasi aktif */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Avatar Video */}
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="avatar-info">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Avatar Video</h2>
          <div className="mt-4 flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <User className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-500">Avatar aktif</p>
              <p className="font-medium text-slate-900">{configState(status, "avatar", statusFailed) === "configured" ? (status.avatar.name || "Avatar Mas Tofa") : "—"}</p>
              <ConfigBadge state={configState(status, "avatar", statusFailed)} activeText="Avatar Mas Tofa dikonfigurasi" testid="avatar-active-status" />
              {configState(status, "avatar", statusFailed) === "configured" && status?.avatar?.id_masked && (
                <p className="mt-2 font-mono text-xs text-slate-400">ID: {status.avatar.id_masked}</p>
              )}
            </div>
          </div>
        </div>

        {/* Voice */}
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="voice-info">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Voice</h2>
          <div className="mt-4 flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <Mic className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-500">Voice aktif</p>
              <p className="font-medium text-slate-900">{configState(status, "voice", statusFailed) === "configured" ? (status.voice.name || "Voice Mas Tofa") : "—"}</p>
              <ConfigBadge state={configState(status, "voice", statusFailed)} activeText="Voice Mas Tofa dikonfigurasi" testid="voice-active-status" />
              {configState(status, "voice", statusFailed) === "configured" && status?.voice?.id_masked && (
                <p className="mt-2 font-mono text-xs text-slate-400">ID: {status.voice.id_masked}</p>
              )}
            </div>
          </div>
        </div>

        {/* Integrasi */}
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="integration-info">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Integrasi</h2>
          <div className="mt-4 flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <Link2 className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-900">HeyGen</p>
              {heygenCreationEnabled(status) ? (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700" data-testid="heygen-connection-status">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  {status?.heygen?.connected ? "✓ Terhubung" : "HeyGen dikonfigurasi"}
                </div>
              ) : (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500" data-testid="heygen-connection-status">
                  <XCircle className="h-3.5 w-3.5 text-slate-400" />
                  Dinonaktifkan sementara
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Format video default */}
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="default-format-info">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Format video default</h2>
          <div className="mt-4 flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <Smartphone className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-900" data-testid="default-format-value">
                {status?.default_aspect_ratio === "16:9" ? "16:9 — Landscape" : "9:16 — Vertikal"}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Anda tetap dapat memilih 9:16 atau 16:9 saat membuat video.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500" data-testid="readonly-note">
        {heygenCreationEnabled(status)
          ? "Avatar pribadi dan suaranya dibuat dan diatur dari menu Avatar Saya."
          : "Pembuatan avatar pribadi sedang dinonaktifkan sementara. Avatar yang sudah ada tetap tersimpan dan dapat dilihat."}
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-5 flex items-start gap-3">
        <SettingsIcon className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Status menunjukkan koneksi integrasi. Nilai kunci API tidak pernah ditampilkan di halaman ini.
        </p>
      </div>

      {/* Temporary: HeyGen Assets (hidden while HeyGen creation is disabled; the endpoints reject requests) */}
      {heygenCreationEnabled(status) && (
      <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8 space-y-6" data-testid="heygen-assets">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900">HeyGen Assets</h2>
          <p className="mt-1 text-sm text-slate-500">
            Muat daftar Avatar & Suara dari HeyGen untuk memilih Avatar III dan suara yang sesuai. Salin ID yang diinginkan.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            data-testid="load-avatars-btn"
            onClick={loadAvatars}
            disabled={loadingAvatars}
            className="bg-blue-600 hover:bg-blue-700 text-white active:scale-95 transition-transform"
          >
            {loadingAvatars ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Load Avatars
          </Button>
          <Button
            data-testid="load-voices-btn"
            onClick={loadVoices}
            disabled={loadingVoices}
            variant="outline"
            className="active:scale-95 transition-transform"
          >
            {loadingVoices ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Load Voices
          </Button>
        </div>

        {avatars && (
          <div className="space-y-2" data-testid="avatars-list">
            <p className="text-sm font-semibold text-slate-700">Avatars ({avatars.length})</p>
            <div className="max-h-96 overflow-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Nama</th>
                    <th className="px-3 py-2 font-medium">Avatar ID</th>
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {avatars.map((a, i) => (
                    <tr key={a.avatar_id || i} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-800">{a.avatar_name || "-"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600 break-all">{a.avatar_id || "-"}</td>
                      <td className="px-3 py-2 text-slate-600">{a.avatar_type || "-"}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`copy-avatar-${a.avatar_id}`}
                          onClick={() => copyId(a.avatar_id)}
                          className="text-blue-600 hover:bg-blue-50"
                        >
                          <Copy className="h-3.5 w-3.5 mr-1" /> Copy ID
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {voices && (
          <div className="space-y-2" data-testid="voices-list">
            <p className="text-sm font-semibold text-slate-700">Voices ({voices.length})</p>
            <div className="max-h-96 overflow-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Nama</th>
                    <th className="px-3 py-2 font-medium">Voice ID</th>
                    <th className="px-3 py-2 font-medium">Bahasa</th>
                    <th className="px-3 py-2 font-medium">Gender</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {voices.map((v, i) => (
                    <tr key={v.voice_id || i} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-800">{v.name || "-"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600 break-all">{v.voice_id || "-"}</td>
                      <td className="px-3 py-2 text-slate-600">{v.language || "-"}</td>
                      <td className="px-3 py-2 text-slate-600">{v.gender || "-"}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`copy-voice-${v.voice_id}`}
                          onClick={() => copyId(v.voice_id)}
                          className="text-blue-600 hover:bg-blue-50"
                        >
                          <Copy className="h-3.5 w-3.5 mr-1" /> Copy ID
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
